/**
 * Client-side dedupe for the `purchase` analytics event, keyed by order id
 * (the event's `transaction_id`).
 *
 * A mount-scoped useRef guard only prevents double-firing within a single
 * page load — reloading or revisiting the order confirmation page re-fired
 * `purchase` and inflated PostHog/GA counts and revenue by ~16% (MWP-899).
 * Persisting fired order ids in localStorage makes the event fire once per
 * order per browser, regardless of which surface renders the tracker or how
 * often it remounts.
 *
 * All functions are SSR-safe no-ops and tolerate unavailable/corrupted
 * storage (private browsing, quota) — in the worst case tracking degrades to
 * the per-mount guard rather than breaking the page.
 */

const STORAGE_KEY = 'newt_tracked_purchases';

/** Upper bound on remembered order ids — oldest are evicted first. */
const MAX_TRACKED = 50;

function readTracked(): string[] {
  if (typeof window === 'undefined') {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((id): id is string => typeof id === 'string');
  } catch {
    return [];
  }
}

/** Whether `purchase` has already fired for this order in this browser. */
export function hasTrackedPurchase(orderId: string): boolean {
  return readTracked().includes(orderId);
}

/** Record that `purchase` fired for this order. */
export function markPurchaseTracked(orderId: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const tracked = readTracked().filter((id) => id !== orderId);
    tracked.push(orderId);
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(tracked.slice(-MAX_TRACKED)),
    );
  } catch {
    // Storage unavailable — fall back to the per-mount guard.
  }
}
